<template>
  <div class="store-pickup-messaging">
    <div v-if="hasPersonalization" class="pickup-message-section">
      <p class="pickup-message-title">
        <i class="icon icon--store" />
        Personalized frames not available for same day store pickup
      </p>
    </div>
    <div v-else-if="!storeName" class="pickup-message-section">
      <p class="pickup-message-title">
        <i class="icon icon--store" />
        Available for Store Pickup
      </p>
      <p class="pickup-message-copy">
        Usually ready in 2-3 hours
      </p>
      <button
        type="button"
        class="change-store-button"
        @click="openStoreSelector"
      >
        Find a Store
      </button>
    </div>
    <div v-else-if="hasStock && hoursAvailable">
      <div v-if="pickupAvailability.availableToday && timeToCutoff > 0" class="pickup-message-section">
        <p class="pickup-message-title">
          <i class="icon icon--store" />
          Free Pickup Today at 
          <button
            type="button"
            class="store-name-button"
            @click="openStoreSelector"
          >
            {{ storeName }}
          </button>
        </p>
        <p class="pickup-message-copy">
          Order within <span class="same-day-cutoff">{{ formattedTimeToCutoff }}</span> get it today
        </p>
        <button
          type="button"
          class="change-store-button"
          @click="openStoreSelector"
        >
          Change Store
        </button>
      </div>
      <div v-else class="pickup-message-section">
        <p class="pickup-message-title">
          <i class="icon icon--store" />
          Pickup at
          <button
            type="button"
            class="store-name-button"
            @click="openStoreSelector"
          >
            {{ storeName }}
          </button>
        </p>
        <p class="pickup-message-copy">
          Ready for Pickup on {{ formattedNextDate }}
        </p>
        <button
          type="button"
          class="change-store-button"
          @click="openStoreSelector"
        >
          Change Store
        </button>
      </div>
    </div>
    <div v-else-if="hasStock" class="pickup-message-section">
      <p class="pickup-message-title">
        <i class="icon icon--store" />
        Pickup at
        <button
          type="button"
          class="store-name-button"
          @click="openStoreSelector"
        >
          {{ storeName }}
        </button>
      </p>
      <p class="pickup-message-copy pickup-message-copy--hours-unavailable">
        Usually ready in 2-3 hours
      </p>
      <button
        type="button"
        class="change-store-button"
        @click="openStoreSelector"
      >
        Change Store
      </button>
    </div>
    <div v-else-if="!hasStock" class="store-pickup-no-stock">
      <p class="pickup-message-title">
        <i class="icon icon--store" />
        Not Available at
        <button
          type="button"
          class="store-name-button"
          @click="openStoreSelector"
        >
          {{ storeName }}
        </button>
      </p>
      <p class="pickup-message-copy">
        Out of Stock
      </p>
      <button
        type="button"
        class="change-store-button"
        @click="openStoreSelector"
      >
        Change Store
      </button>
    </div>
  </div>
</template>

<script>
import { checkPickupAvailability, hasStoreHours } from "../../helpers/pickupAvailability";

export default {
  name: "StorePickupMessaging",
  emits: ["open-store-selector"],
  props: {
    variantId: {
      type: [String, Number],
      required: true
    },
    selectedStore: {
      type: Object,
      default: null,
      required: false
    },
    frameSpec: {
      type: Object,
      required: false,
      default: null
    },
    itemProperties: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      isLoading: false,
      error: null,
      hasStock: false,
      storeAvailability: null,
      pickupAvailability: null,
      countdownInterval: null,
      currentTimeToCutoff: null
    };
  },
  computed: {
    storeName() {
      // Stockist uses "name" for display name
      return this.selectedStore?.name || "";
    },
    shopifyStoreId() {
      if (!this.selectedStore?.custom_fields) {
        return "";
      }
      const storeId = this.selectedStore.custom_fields.find(
        (field) => field.name === "shopify_store_id"
      );
      return storeId?.value || "";
    },
    hoursAvailable() {
      return hasStoreHours(this.pickupAvailability);
    },
    hasPersonalization() {
      if (this.frameSpec) {
        // if we have a framespec, check for personalization there that can't be done in store
        return this.frameSpec.hasPersonalizationNotAvailableInStore();
      } else if (this.itemProperties?.Includes) {
        // if we don't (bundles), check for the add ons in the item properties. don't block story pockets, ornaments have them
        const personalizedAddOns = ["brass plate", "moulding engraving", "mat caption"];
        const includesArray = this.itemProperties.Includes.split(",").map((item) => item.trim().toLowerCase());
        return includesArray.some((include) => personalizedAddOns.includes(include));
      } else {
        return false;
      }
    },
    timeToCutoff() {
      // Use live countdown value if available, otherwise use initial value
      if (this.currentTimeToCutoff !== null) {
        return this.currentTimeToCutoff;
      }
      return this.pickupAvailability?.timeToCutoff || 0;
    },
    formattedTimeToCutoff() {
      const minutes = Math.floor(this.timeToCutoff);
      if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (mins === 0) {
          return `${hours} hour${hours > 1 ? 's' : ''}`;
        }
        return `${hours}hrs ${mins}min`;
      }
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    },
    formattedNextDate() {
      if (!this.pickupAvailability?.nextAvailableDate) return "";

      // Parse date parts directly to avoid timezone issues
      const [year, month, day] = this.pickupAvailability.nextAvailableDate.split("-");
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    },
  },
  watch: {
    selectedStore: {
      handler(newVal) {
        if (newVal && this.variantId) {
          this.checkAvailability();
        }
      },
      immediate: true,
      deep: true,
    }
  },
  mounted() {
    if (this.variantId && this.shopifyStoreId) {
      this.checkAvailability();
    }
  },
  beforeUnmount() {
    this.clearCountdown();
  },
  methods: {
    async checkAvailability() {
      this.isLoading = true;
      this.error = null;
      this.clearCountdown();

      try {
        const result = await checkPickupAvailability(this.variantId, this.shopifyStoreId);

        this.hasStock = result.hasStock;
        this.storeAvailability = result.storeAvailability;
        this.pickupAvailability = result.pickupAvailability;
        this.error = result.error || null;

        // Start countdown if available today
        if (this.hasStock && this.pickupAvailability?.availableToday && this.pickupAvailability?.timeToCutoff > 0) {
          this.startCountdown();
        }
      } catch (err) {
        console.error('Error checking pickup availability:', err);
        this.error = err;
      } finally {
        this.isLoading = false;
      }
    },
    startCountdown() {
      this.currentTimeToCutoff = this.pickupAvailability?.timeToCutoff || 0;

      this.countdownInterval = setInterval(() => {
        this.currentTimeToCutoff -= 1;

        if (this.currentTimeToCutoff <= 0) {
          this.clearCountdown();
          // Refresh availability when countdown expires
          this.checkAvailability();
        }
      }, 300000); // Update every 5 minutes
    },
    clearCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    },
    openStoreSelector() {
      this.$emit("open-store-selector");
    },
  },
};
</script>

<style lang="scss">
.store-pickup-messaging {
  margin-top: 25px;

  p {
    margin: 0 0 3px 0;
  }

  .pickup-message-title {
    align-items: center;
    color: #000;
    display: flex;
    font-family: "Apercu";
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    gap: 4px;
    letter-spacing: 0.28px;
    line-height: 23px;

    .icon--store {
      align-items: center;
      display: inline-flex;
      margin: 0 4px 3px 0;
    }
  }

  button.change-store-button,
  button.store-name-button {
    background: none;
    border: none;
    cursor: pointer;
    font-family: "Apercu";
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    letter-spacing: 0.28px;
    line-height: 23px;
    padding: 0;
    text-decoration: underline;
  }

  button.change-store-button {
    font-size: 12px;
    font-weight: 400;
  }

  .pickup-message-copy {
    font-family: "Apercu";
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    letter-spacing: 0.24px;
    line-height: 24px;
  }

  // .pickup-message-copy--hours-unavailable
  .pickup-message-copy--hours-unavailable {
    color: #008048;
  }

  .same-day-cutoff {
    color: #008048;
  }
}
</style>
